'use client';
import { cn } from '@/app/lib/utils';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import React from 'react';

export type TooltipProps = {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'center' | 'start' | 'end';
  sideOffset?: number;
  alignOffset?: number;
  delayDuration?: number;
  className?: string;
  arrowClassName?: string;
  disableHoverableContent?: boolean;
  onPointerDownOutside?: (event: any) => void;
  persistOnClick?: boolean;
};

export default function Tooltip({
  label,
  children,
  side = 'right',
  align = 'center',
  sideOffset = 8,
  delayDuration = 100,
  alignOffset = 0,
  disableHoverableContent,
  onPointerDownOutside,
  persistOnClick = false,
  className = '',
  arrowClassName = '',
}: TooltipProps) {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
      >
        <RadixTooltip.Trigger
          asChild
          onPointerDownCapture={e => {
            if (persistOnClick) {
              e.preventDefault();
            }
          }}
          onClickCapture={e => {
            if (persistOnClick) {
              e.preventDefault();
            }
          }}
        >
          {children}
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            data-slot="tooltip-content"
            side={side}
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            onPointerDownOutside={e => {
              if (persistOnClick) {
                e.preventDefault();
              }
              onPointerDownOutside?.(e);
            }}
            className={cn(
              'bg-background rounded-xl shadow-lg px-4 py-2 text-sm select-none z-50',
              'origin-[var(--radix-tooltip-content-transform-origin)]',
              'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
              'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
              'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              className
            )}
          >
            {label}
            <RadixTooltip.Arrow className={cn('fill-background', arrowClassName)} />
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}

export const TooltipProvider = RadixTooltip.Provider;
export const TooltipRoot = RadixTooltip.Root;
export const TooltipTrigger = RadixTooltip.Trigger;
export const TooltipContent = RadixTooltip.Content;
